import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { ClipboardList, Plus, Pencil, Trash2, ArrowUp, ArrowDown, ExternalLink, History } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = { title: '', url: '', description: '' };

export default function AdminDutyReportsPage() {
  const [reports, setReports] = useState([]);
  const [opens, setOpens] = useState([]);
  const [filterReport, setFilterReport] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchReports = useCallback(async () => {
    const res = await axios.get(`${API}/admin/duty-reports`);
    setReports(res.data || []);
  }, []);

  const fetchOpens = useCallback(async () => {
    const params = filterReport !== 'all' ? `?report_id=${filterReport}` : '';
    const res = await axios.get(`${API}/admin/duty-reports-opens${params}`);
    setOpens(res.data || []);
  }, [filterReport]);

  useEffect(() => { fetchReports().catch(() => toast.error('Failed to load reports')); }, [fetchReports]);
  useEffect(() => { fetchOpens().catch(() => {}); }, [fetchOpens]);

  const startAdd = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const startEdit = (r) => { setEditing(r); setForm({ title: r.title, url: r.url, description: r.description || '' }); setDialogOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/duty-reports/${editing.id}`, form);
        toast.success('Report updated');
      } else {
        await axios.post(`${API}/admin/duty-reports`, form);
        toast.success('Report added');
      }
      setDialogOpen(false);
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (r) => {
    try {
      await axios.put(`${API}/admin/duty-reports/${r.id}`, { active: !r.active });
      fetchReports();
    } catch { toast.error('Failed to update'); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? Employees will no longer see it.`)) return;
    try {
      await axios.delete(`${API}/admin/duty-reports/${r.id}`);
      toast.success('Report deleted');
      fetchReports();
    } catch { toast.error('Failed to delete'); }
  };

  const move = async (idx, dir) => {
    const other = idx + dir;
    if (other < 0 || other >= reports.length) return;
    const a = reports[idx]; const b = reports[other];
    try {
      await Promise.all([
        axios.put(`${API}/admin/duty-reports/${a.id}`, { order: b.order }),
        axios.put(`${API}/admin/duty-reports/${b.id}`, { order: a.order }),
      ]);
      fetchReports();
    } catch { toast.error('Failed to reorder'); }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-6 max-w-5xl mx-auto">
        <div className="border-b border-border pb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-2xl font-barlow tracking-widest text-foreground">DUTY REPORTS</h1>
              <p className="text-xs text-muted-foreground mt-1">Manage the report links employees see in their portal.</p>
            </div>
          </div>
          <Button onClick={startAdd} data-testid="add-report-btn" className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
            <Plus className="w-4 h-4 mr-2" /> Add Report
          </Button>
        </div>

        <div className="space-y-2">
          {reports.length === 0 && (
            <Card className="rounded-sm border-border">
              <CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="admin-reports-empty">
                No report links yet — add the first one.
              </CardContent>
            </Card>
          )}
          {reports.map((r, idx) => (
            <Card key={r.id} className="rounded-sm border-border" data-testid={`admin-report-row-${r.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`move-up-${r.id}`} className="text-muted-foreground hover:text-primary disabled:opacity-20">
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button onClick={() => move(idx, 1)} disabled={idx === reports.length - 1} data-testid={`move-down-${r.id}`} className="text-muted-foreground hover:text-primary disabled:opacity-20">
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-barlow tracking-widest uppercase text-foreground">{r.title}</p>
                    {!r.active && <Badge variant="outline" className="bg-zinc-500/15 text-zinc-400 border-zinc-500/40 text-[10px]">HIDDEN</Badge>}
                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-[10px]">{r.open_count || 0} opens</Badge>
                  </div>
                  {r.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{r.description}</p>}
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 mt-0.5">
                    <ExternalLink className="w-3 h-3" /> {r.url}
                  </a>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <Switch checked={!!r.active} onCheckedChange={() => toggleActive(r)} data-testid={`toggle-active-${r.id}`} />
                  <Button variant="outline" size="icon" onClick={() => startEdit(r)} data-testid={`edit-report-${r.id}`} className="rounded-sm h-8 w-8">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => remove(r)} data-testid={`delete-report-${r.id}`} className="rounded-sm h-8 w-8 text-red-400 hover:text-red-300">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="border-t border-border pt-5 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <h2 className="text-lg font-barlow tracking-widest text-foreground">OPEN LOG</h2>
            </div>
            <Select value={filterReport} onValueChange={setFilterReport}>
              <SelectTrigger className="w-56 rounded-sm h-9" data-testid="opens-filter-report">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All reports</SelectItem>
                {reports.map((r) => <SelectItem key={r.id} value={r.id}>{r.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">Who opened which report and when. Note: opening a link doesn't guarantee the form was submitted.</p>
          {opens.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4" data-testid="opens-empty">No opens recorded yet.</p>
          ) : (
            <div className="space-y-1.5" data-testid="opens-list">
              {opens.map((o) => (
                <div key={o.id} className="flex items-center gap-3 text-sm border border-border rounded-sm px-3 py-2">
                  <span className="text-foreground font-medium">{o.user_name}</span>
                  <span className="text-muted-foreground text-xs">opened</span>
                  <span className="text-primary">{o.report_title}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {(() => { try { return format(parseISO(o.at), 'dd MMM yyyy, HH:mm'); } catch { return o.at; } })()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-widest">{editing ? 'EDIT REPORT' : 'ADD REPORT'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Shift Report" data-testid="report-title-input" className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Link (URL) *</Label>
                <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" data-testid="report-url-input" className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description (optional)</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short note shown to employees" data-testid="report-description-input" className="rounded-sm" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} disabled={saving || !form.title.trim() || !form.url.trim()} data-testid="save-report-btn" className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
